import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, AlertTriangle, Flag, ChevronRight, Reply, CornerUpRight, Archive, Delete, ArrowLeft, Eye, Star, FileText, EyeOff, Check, X, FileSpreadsheet, Image, Download, Edit3 } from "lucide-react";
import { FontWrapper } from "./common/FontWrapper";
import { EditableText } from "./common/EditableText";
import { useIsMobile } from "./ui/use-mobile";
import { PhishingReportButton } from "./ui/PhishingReportButton";
import { PhishingReportModal } from "./ui/PhishingReportModal";
import { PhishingResultModal } from "./ui/PhishingResultModal";
import { AttachmentPreviewModal } from "./ui/AttachmentPreviewModal";
import { AttachmentEditModal } from "./ui/AttachmentEditModal";
import { InboxSceneConfig, EmailData, EmailAttachment } from "../data/inboxConfig";
import { useEditMode } from "../contexts/EditModeContext";
import { usePresentationMode } from "../contexts/PresentationModeContext";
import { useRegisterCardSteps } from "../contexts/CardNavContext";
import { inboxCardLayout, parseSender, senderInitial } from "../presentation/inboxCardLayout";
import { enhanceLinkTooltips } from "../utils/linkTooltip";
import { EmailContentWithPortalTooltips } from "./ui/EmailContentWithTooltips";
import { RichTextEditor } from "./ui/RichTextEditor";

interface InboxProps {
  config: InboxSceneConfig;
  onNextSlide?: () => void;
  onEmailReport?: (emailId: string, isCorrect: boolean) => void;
  onAllEmailsReported?: (allReported: boolean) => void;
  selectedLanguage?: string;
  // External control for selected email
  externalSelectedEmailId?: string | null;
  onSelectedEmailIdChange?: (id: string | null) => void;
  onEmailSelect?: (emailId: string) => void;
  // Edit mode prop
  isEditMode?: boolean;
}



export function Inbox({ config, onEmailReport, onAllEmailsReported, selectedLanguage, externalSelectedEmailId, onSelectedEmailIdChange, onEmailSelect, isEditMode: propIsEditMode }: InboxProps) {
  // Load initial state from localStorage
  const loadInboxState = () => {
    try {
      const saved = localStorage.getItem('inbox-state');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          selectedEmailId: parsed.selectedEmailId || null,
          reportedEmails: new Set<string>(parsed.reportedEmails || []),
          reportResults: new Map<string, boolean>(parsed.reportResults || []),
          accuracy: parsed.accuracy || 0,
          totalReports: parsed.totalReports || 0,
          showHeaders: parsed.showHeaders || false
        };
      }
    } catch (error) {
      console.warn('Failed to load inbox state:', error);
    }
    return {
      selectedEmailId: null,
      reportedEmails: new Set<string>(),
      reportResults: new Map<string, boolean>(),
      accuracy: 0,
      totalReports: 0,
      showHeaders: false
    };
  };

  const initialState = loadInboxState();
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(initialState.selectedEmailId);
  const [reportedEmails, setReportedEmails] = useState<Set<string>>(initialState.reportedEmails);
  const [reportResults, setReportResults] = useState<Map<string, boolean>>(initialState.reportResults);
  const [accuracy, setAccuracy] = useState(initialState.accuracy);
  const [totalReports, setTotalReports] = useState(initialState.totalReports);
  const [showHeaders, setShowHeaders] = useState(initialState.showHeaders);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [lastReportedEmail, setLastReportedEmail] = useState<EmailData | null>(null);
  const [lastReportCorrect, setLastReportCorrect] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<EmailAttachment | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState<{ emailIndex: number; attIndex: number } | null>(null);
  const [showAttachmentEditModal, setShowAttachmentEditModal] = useState(false);
  const [isEditingEmailContent, setIsEditingEmailContent] = useState(false);
  const [emailEditorValue, setEmailEditorValue] = useState<string>('');
  let updateTempConfigFn: ((path: string, value: any) => void) | null = null;
  const isMobile = useIsMobile();
  const isCardPresentation = usePresentationMode() === "cards";
  const oneMailView = isMobile || isCardPresentation;
  const emailContentRef = useRef<HTMLDivElement>(null);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      const stateToSave = {
        selectedEmailId,
        reportedEmails: Array.from(reportedEmails),
        reportResults: Array.from(reportResults.entries()),
        accuracy,
        totalReports,
        showHeaders
      };
      localStorage.setItem('inbox-state', JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save inbox state:', error);
    }
  }, [selectedEmailId, reportedEmails, reportResults, accuracy, totalReports, showHeaders]);


  // Notify parent when all emails have been reported
  useEffect(() => {
    const allReviewed = reportedEmails.size === config.emails.length;
    onAllEmailsReported?.(allReviewed);
  }, [reportedEmails, config.emails, onAllEmailsReported]);

  // Cleanup localStorage when page/app is being closed or refreshed
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      try {
        localStorage.removeItem('inbox-state');
      } catch (error) {
        console.warn('Failed to cleanup inbox state:', error);
      }
    };

    const handleUnload = () => {
      try {
        localStorage.removeItem('inbox-state');
      } catch (error) {
        console.warn('Failed to cleanup inbox state:', error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        try {
          localStorage.removeItem('inbox-state');
        } catch (error) {
          console.warn('Failed to cleanup inbox state:', error);
        }
      }
    };

    // Multiple event listeners to ensure cleanup
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Don't cleanup localStorage on component unmount - keep state for when user returns
    };
  }, []);


  // Edit mode - always call hook, but use prop if provided
  let contextIsEditMode = false;
  try {
    const editModeContext = useEditMode();
    contextIsEditMode = editModeContext.isEditMode;
    updateTempConfigFn = editModeContext.updateTempConfig;
  } catch (error) {
    // EditModeProvider not available, default to false
    contextIsEditMode = false;
  }

  const isEditMode = propIsEditMode !== undefined ? propIsEditMode : contextIsEditMode;
  const selectedEmail = config.emails.find(email => email.id === selectedEmailId);

  useEffect(() => {
    if (!isCardPresentation) return;
    if (selectedEmailId || !config.emails.length) return;
    const firstId = config.emails[0].id;
    setSelectedEmailId(firstId);
    onSelectedEmailIdChange?.(firstId);
  }, [isCardPresentation, selectedEmailId, config.emails, onSelectedEmailIdChange]);

  const cardEmailIndex = Math.max(0, config.emails.findIndex((email) => email.id === selectedEmailId));
  useRegisterCardSteps(
    isCardPresentation && config.emails.length > 0,
    cardEmailIndex,
    config.emails.length,
    false,
    () => {
      if (cardEmailIndex < config.emails.length - 1) {
        const nextId = config.emails[cardEmailIndex + 1].id;
        setSelectedEmailId(nextId);
        onSelectedEmailIdChange?.(nextId);
        return true;
      }
      return false;
    },
    () => {
      if (cardEmailIndex > 0) {
        const prevId = config.emails[cardEmailIndex - 1].id;
        setSelectedEmailId(prevId);
        onSelectedEmailIdChange?.(prevId);
        return true;
      }
      return false;
    }
  );


  // Check if all emails are reported
  const allEmailsReported = useMemo(() => {
    return reportedEmails.size === config.emails.length;
  }, [config.emails, reportedEmails]);



  // Handle attachment preview
  const handleAttachmentClick = useCallback((attachment: EmailAttachment) => {
    setPreviewAttachment(attachment);
    setShowPreviewModal(true);
  }, []);

  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewAttachment(null);
  }, []);

  // Phishing Report icon component
  const PhishingIcon = () => (
    <svg width="28" height="16" viewBox="0 0 29 16" fill="none" className="flex-shrink-0">
      <path d="M0.521774 12.6882C0.504807 12.7999 0.663163 12.8395 0.71265 12.7377C2.74159 8.62464 6.8829 -0.74668 19.7098 3.13164C19.7098 3.13164 20.462 3.60106 19.6165 4.9174C19.6165 4.9174 20.3149 4.85094 20.8989 4.59361C21.2905 4.42253 21.7133 4.34335 22.1417 4.32922C22.8797 4.30518 23.9585 3.62085 23.5089 2.29603C23.1597 1.26671 22.1558 0.927376 21.0996 0.667219C16.513 -0.4639 2.74159 -1.92163 0.521774 12.6882Z" fill="currentColor" />
      <path d="M3.96289 8.32319C3.96289 8.32319 13.5265 -1.99261 25.6549 10.6264C26.0664 11.0548 26.1554 11.2443 26.7125 11.4493C27.1508 11.6105 28.2013 11.4705 26.3364 9.26344L25.9193 8.7516C25.7256 8.47024 25.9193 8.17049 26.3124 8.35854C26.5202 8.45751 26.8525 8.62577 26.8525 8.62577C27.9101 9.36948 28.1795 9.69552 28.4276 9.94493C28.4276 9.94493 30.284 12.1068 27.3106 12.4716C27.3106 12.4716 27.4166 17.5828 23.5058 13.4231L23.1636 12.9933C23.0873 12.8972 22.9558 12.4716 23.3785 12.5069C23.7009 12.5338 24.1972 12.7331 25.0455 13.2492C25.0455 13.2492 27.4265 14.2573 24.7599 11.2839C24.7627 11.2839 17.1602 1.89561 3.96289 8.32319Z" fill="currentColor" />
      <path d="M1.72279 15.8465C3.80829 16.1604 11.4023 16.7387 13.1542 8.90568C13.2065 8.67521 12.9138 8.52534 12.7568 8.70349C11.3104 10.3535 7.85486 14.2728 5.23208 13.7073C5.23208 13.7073 5.62797 13.5291 5.58414 11.9569C5.5799 11.824 5.40882 11.7716 5.33247 11.8805C5.04403 12.2905 4.41202 13.3411 3.48026 13.5489C2.1512 13.8472 0.0713525 14.0692 0.728815 15.206C0.938072 15.5623 1.31558 15.7843 1.72279 15.8465Z" fill="currentColor" />
      <path d="M2.742 12.9313C3.26128 12.9313 3.68224 12.5103 3.68224 11.991C3.68224 11.4717 3.26128 11.0508 2.742 11.0508C2.22272 11.0508 1.80176 11.4717 1.80176 11.991C1.80176 12.5103 2.22272 12.9313 2.742 12.9313Z" fill="currentColor" />
    </svg>
  );

  // Attachment component with proper icons
  const AttachmentIcon = ({ type }: { type: EmailAttachment['type'] }) => {
    switch (type) {
      case 'pdf':
      case 'doc':
        return <FileText size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
      case 'xls':
        return <FileSpreadsheet size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
      case 'img':
        return <Image size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
      case 'zip':
        return <Archive size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
      case 'exe':
        return <AlertTriangle size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
      case 'txt':
        return <FileText size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
      default:
        return <FileText size={16} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />;
    }
  };

  const AttachmentComponent = ({ attachments }: { attachments: EmailAttachment[] }) => (
    <div className="border-t border-white/50 dark:border-white/10 pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Download size={14} className="text-[#1C1C1E] dark:text-[#F2F2F7]" />
        <FontWrapper>
          <span className="text-sm font-medium text-[#1C1C1E] dark:text-[#F2F2F7]">
            {config.texts.attachmentsLabel ?? 'Attachments'} ({attachments.length})
          </span>
        </FontWrapper>
      </div>
      <div className="space-y-2">
        {attachments.map((attachment, attIndex) => (
          <motion.div
            key={attachment.id}
            whileHover={{ x: 2 }}
            onClick={() => handleAttachmentClick(attachment)}
            className={`flex items-center justify-between p-3 ${isEditMode ? 'glass-border-2-no-overflow' : 'glass-border-2'} cursor-pointer transition-all hover:scale-[1.02]`}
          >
            <div className="flex items-center gap-3">
              <AttachmentIcon type={attachment.type} />
              <div>
                <FontWrapper>
                  <p className="text-sm font-medium text-[#1C1C1E] dark:text-[#F2F2F7]">
                    {attachment.name}
                  </p>
                  <p className="text-xs text-[#1C1C1E]/70 dark:text-[#F2F2F7]/70">
                    {attachment.size}
                  </p>
                </FontWrapper>
              </div>
            </div>
            {isEditMode && (
              <div className="flex items-center gap-2">
                <button
                  className={`p-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded-full`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const emailIndex = selectedEmail ? config.emails.findIndex(e => e.id === selectedEmail.id) : -1;
                    if (emailIndex >= 0) {
                      setEditingAttachment({ emailIndex, attIndex });
                      setShowAttachmentEditModal(true);
                    }
                  }}
                  aria-label="Edit attachment content"
                >
                  <Edit3 className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );

  // Parent starts at null meaning "uncontrolled". Null must not fight card auto-select.
  useEffect(() => {
    if (typeof externalSelectedEmailId !== "string") return;
    if (externalSelectedEmailId !== selectedEmailId) {
      setSelectedEmailId(externalSelectedEmailId);
    }
  }, [externalSelectedEmailId, selectedEmailId]);

  const handleEmailSelect = useCallback((emailId: string) => {
    setSelectedEmailId(emailId);
    onSelectedEmailIdChange?.(emailId);
    onEmailSelect?.(emailId);


    // Scroll to top of email content
    if (emailContentRef.current) {
      emailContentRef.current.scrollTop = 0;
    }
    // Open inline editor by default when in edit mode
    const email = config.emails.find(e => e.id === emailId);
    if (email && (propIsEditMode ?? contextIsEditMode)) {
      setEmailEditorValue(email.content || '');
      setIsEditingEmailContent(true);
    } else {
      setIsEditingEmailContent(false);
    }
  }, [onSelectedEmailIdChange, onEmailSelect, config.emails, propIsEditMode, contextIsEditMode]);

  const handleEmailReport = useCallback((emailId: string) => {
    const email = config.emails.find(e => e.id === emailId);
    if (!email || reportedEmails.has(emailId)) return;

    const isCorrect = email.isPhishing;
    const newReportedEmails = new Set(reportedEmails);
    newReportedEmails.add(emailId);
    setReportedEmails(newReportedEmails);

    const newReportResults = new Map(reportResults);
    newReportResults.set(emailId, isCorrect);
    setReportResults(newReportResults);

    const newTotalReports = totalReports + 1;
    setTotalReports(newTotalReports);

    const correctReports = Array.from(newReportedEmails).filter(id => {
      const reportedEmail = config.emails.find(e => e.id === id);
      return reportedEmail?.isPhishing;
    }).length;

    const newAccuracy = Math.round((correctReports / newTotalReports) * 100);
    setAccuracy(newAccuracy);

    onEmailReport?.(emailId, isCorrect);
  }, [config.emails, reportedEmails, reportResults, totalReports, onEmailReport]);

  const handlePhishingReportClick = useCallback(() => {
    if (selectedEmail) {
      setShowReportModal(true);
    }
  }, [selectedEmail]);


  const handleModalReport = useCallback((selectedOptions: number[]) => {
    if (selectedEmail) {
      const isCorrect = selectedEmail.isPhishing;
      setLastReportedEmail(selectedEmail);
      setLastReportCorrect(isCorrect);
      handleEmailReport(selectedEmail.id);
      setShowReportModal(false);
      // Show result modal after a brief delay
      setTimeout(() => {
        setShowResultModal(true);
      }, 300);
    }
  }, [selectedEmail, handleEmailReport]);

  return (
    <div key={selectedLanguage} className={isCardPresentation ? "flex min-h-0 w-full flex-1 flex-col" : undefined} data-testid={isCardPresentation ? "inbox-card-face" : "inbox-classic"}>
      {/* Single Card with Header and Two-Column Layout */}
      <div className={isCardPresentation
        ? "flex min-h-0 w-full flex-1 flex-col"
        : `backdrop-blur-xl bg-white/10 dark:bg-black/10 rounded-lg border border-white/20 dark:border-white/10 ${isEditMode ? '' : 'overflow-hidden'} flex flex-col`}>
        {/* Header - Desktop layout (classic only; card mode is one mail filling the shell) */}
        <div className={`${isCardPresentation ? "hidden" : "hidden lg:block"} p-6 border-b border-white/20 dark:border-white/10`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              <FontWrapper>
                <h1 className="text-base font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">{config.texts.title}</h1>
              </FontWrapper>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#1C1C1E] dark:text-[#F2F2F7] font-semibold">
              <span>{config.texts.reportsLabel}: {totalReports}</span>
              <span>{config.texts.accuracyLabel}: {accuracy}%</span>
            </div>
          </div>
        </div>

        {/* Header - Mobile layout */}
        <div className={`${isCardPresentation ? "hidden" : "lg:hidden"} p-4`}>
          <div className="flex items-center justify-between pb-4 border-b border-white/20 dark:border-white/10 mb-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              <FontWrapper>
                <h1 className="text-sm font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">{config.texts.mobileTitle}</h1>
              </FontWrapper>
            </div>
            <div className="text-sm text-[#1C1C1E] dark:text-[#F2F2F7] font-semibold">
              {config.texts.accuracyLabel}: {accuracy}%
            </div>
          </div>
          {/* Phishing Report Button - Mobile */}
          <div className="pb-4 border-b border-white/20 dark:border-white/10 mx-4">
            <PhishingReportButton
              text={config.texts.phishingReportLabel}
              onClick={handlePhishingReportClick}
              disabled={!selectedEmail || allEmailsReported}
              icon={<PhishingIcon />}
            />
          </div>
        </div>

        {/* Toolbar row - Desktop only */}
        <div className={`${isCardPresentation ? "hidden" : "hidden lg:block"} px-6 py-3 border-b border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/5`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Email toolbar icons */}
              <button className={`p-3 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'}`}>
                <Reply className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              </button>
              <button className={`p-3 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'}`}>
                <CornerUpRight className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              </button>
              <button className={`p-3 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'}`}>
                <Archive className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              </button>
              <button className={`p-3 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'}`}>
                <Delete className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              </button>
              <button className={`p-3 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'}`}>
                <Flag className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
              </button>
            </div>
            <PhishingReportButton
              text={config.texts.phishingReportLabel}
              onClick={handlePhishingReportClick}
              disabled={!selectedEmail || allEmailsReported}
              icon={<PhishingIcon />}
            />
          </div>
        </div>

        {/* Content - Mobile: Conditional view, Desktop: Two-column */}
        <div className={isCardPresentation
          ? "flex min-h-0 w-full flex-1 flex-col"
          : `grid grid-cols-1 lg:grid-cols-3 flex-1 min-h-0 ${isEditMode ? '' : 'overflow-hidden'}`}>
          {/* Mobile: Show email list or detail view */}
          <div className={isCardPresentation
            ? "flex min-h-0 w-full flex-1 flex-col"
            : "lg:border-r border-white/20 dark:border-white/10 flex flex-col min-h-0"}>
            {/* Mobile: Back to Inbox button when email is selected */}
            {oneMailView && selectedEmail && !isCardPresentation && (
              <div className="px-4 py-3 border-b border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/5">
                <button
                  onClick={() => {
                    setSelectedEmailId(null);
                    onSelectedEmailIdChange?.(null);
                  }}
                  className="flex items-center gap-2 text-sm text-[#1C1C1E] dark:text-[#F2F2F7] hover:text-[#1C1C1E] dark:hover:text-[#F2F2F7] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <FontWrapper>
                    <span>{config.texts.backToInboxText}</span>
                  </FontWrapper>
                </button>
              </div>
            )}

            {/* Mobile: Email detail view or Email list */}
            {oneMailView && selectedEmail ? (
              <div className={isCardPresentation ? inboxCardLayout.shell : "p-4 overflow-y-auto flex-1"}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedEmail.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.22 }}
                    className={isCardPresentation ? "flex min-h-0 flex-1 flex-col" : undefined}
                    data-testid={isCardPresentation ? "inbox-card-message" : undefined}
                  >
                    <div className={isCardPresentation ? inboxCardLayout.scroller : undefined}>
                    {/* Email Header */}
                    {isCardPresentation ? (
                      <div>
                        <div className={inboxCardLayout.senderRow}>
                          <div className={inboxCardLayout.avatar} aria-hidden="true">
                            {senderInitial(selectedEmail.sender)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <FontWrapper className={inboxCardLayout.senderName}>
                                {isEditMode ? (
                                  <EditableText
                                    configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.sender`}
                                    className={inboxCardLayout.senderName}
                                    placeholder="Enter sender name"
                                  >
                                    {parseSender(selectedEmail.sender).name}
                                  </EditableText>
                                ) : (
                                  parseSender(selectedEmail.sender).name
                                )}
                              </FontWrapper>
                              <FontWrapper className={inboxCardLayout.timestamp}>
                                {isEditMode ? (
                                  <EditableText
                                    configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.timestamp`}
                                    className={inboxCardLayout.timestamp}
                                    placeholder="Enter timestamp"
                                  >
                                    {selectedEmail.timestamp}
                                  </EditableText>
                                ) : (
                                  selectedEmail.timestamp
                                )}
                              </FontWrapper>
                            </div>
                            {parseSender(selectedEmail.sender).address ? (
                              <p className={inboxCardLayout.senderAddress}>{parseSender(selectedEmail.sender).address}</p>
                            ) : null}
                          </div>
                        </div>
                        <FontWrapper>
                          {isEditMode ? (
                            <EditableText
                              configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.subject`}
                              className={`${inboxCardLayout.subject} block`}
                              placeholder="Enter email subject"
                            >
                              {selectedEmail.subject}
                            </EditableText>
                          ) : (
                            <h3 className={inboxCardLayout.subject}>{selectedEmail.subject}</h3>
                          )}
                        </FontWrapper>
                      </div>
                    ) : (
                    <div className="border-b border-white/20 dark:border-white/10 pb-4 mb-6">
                      <FontWrapper>
                        {isEditMode ? (
                          <EditableText
                            configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.subject`}
                            className="text-lg font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] mb-2 block"
                            placeholder="Enter email subject"
                          >
                            {selectedEmail.subject}
                          </EditableText>
                        ) : (
                          <h3 className="text-lg font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] mb-2">{selectedEmail.subject}</h3>
                        )}
                        <div className="text-sm text-[#1C1C1E] dark:text-[#F2F2F7] space-y-1">
                          <p><span className="font-semibold">{config.texts.fromLabel ?? "From:"}</span> {isEditMode ? (
                            <EditableText
                              configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.sender`}
                              className="inline text-sm text-[#1C1C1E] dark:text-[#F2F2F7]"
                              placeholder="Enter sender name"
                            >
                              {selectedEmail.sender}
                            </EditableText>
                          ) : selectedEmail.sender}</p>
                          <p><span className="font-semibold">{config.texts.timeLabel ?? "Time:"}</span> {isEditMode ? (
                            <EditableText
                              configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.timestamp`}
                              className="inline text-sm text-[#1C1C1E] dark:text-[#F2F2F7]"
                              placeholder="Enter timestamp"
                            >
                              {selectedEmail.timestamp}
                            </EditableText>
                          ) : selectedEmail.timestamp}</p>
                        </div>
                      </FontWrapper>
                    </div>
                    )}

                    {/* Headers button */}
                    <div className={isCardPresentation ? "" : "mb-4"}>
                      <button
                        onClick={() => setShowHeaders(!showHeaders)}
                        className={isCardPresentation
                          ? inboxCardLayout.headersToggle
                          : `flex items-center gap-2 px-3 py-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} text-sm font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] hover:bg-white/5 dark:hover:bg-white/5 transition-colors`}
                      >
                        {showHeaders ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        <FontWrapper>{config.texts.headersButtonText}</FontWrapper>
                      </button>
                    </div>

                    {/* Email Headers - Mobile */}
                    {showHeaders && selectedEmail.headers && (
                      <div className="mb-6 border-b border-white/20 dark:border-white/10 pb-4">
                        <FontWrapper>
                          <h4 className="text-sm font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] mb-3">{config.texts.emailHeadersTitle}</h4>
                          <div className={`${isEditMode ? 'glass-border-2-no-overflow' : 'glass-border-2'} rounded-lg p-3`}>
                            {selectedEmail.headers.map((header, index) => (
                              <div key={index} className="text-xs text-[#1C1C1E] dark:text-[#F2F2F7] mb-1">
                                {isEditMode ? (
                                  <EditableText
                                    configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.headers.${index}`}
                                    className="text-xs text-[#1C1C1E] dark:text-[#F2F2F7] block"
                                    placeholder="Enter email header..."
                                  >
                                    {header}
                                  </EditableText>
                                ) : (
                                  header
                                )}
                              </div>
                            ))}
                          </div>
                        </FontWrapper>
                      </div>
                    )}

                    {/* Email Content */}
                    <div className={`${isCardPresentation ? inboxCardLayout.body : "prose prose-sm max-w-none mb-6"} prose-p:text-[#1C1C1E] dark:prose-p:text-[#F2F2F7] prose-p:mb-1 prose-div:mb-1 prose-strong:text-[#1C1C1E] dark:prose-strong:text-[#F2F2F7] prose-li:text-[#1C1C1E] dark:prose-li:text-[#F2F2F7] prose-a:text-[#1C1C1E] dark:prose-a:text-[#F2F2F7] email-content-container`}>
                      <FontWrapper>
                        {isEditMode ? (
                          <div>
                            <div className="flex justify-end mb-2 gap-2">
                              {isEditingEmailContent ? (
                                <>
                                  <button
                                    className={`px-3 py-1 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded`}
                                    onClick={() => {
                                      const emailIndex = config.emails.findIndex(e => e.id === selectedEmail.id);
                                      if (emailIndex >= 0 && updateTempConfigFn) {
                                        updateTempConfigFn(`emails.${emailIndex}.content`, emailEditorValue);
                                      }
                                      setIsEditingEmailContent(false);
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className={`px-3 py-1 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded`}
                                    onClick={() => setIsEditingEmailContent(false)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  className={`p-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded-full`}
                                  onClick={() => {
                                    setEmailEditorValue(selectedEmail.content || '');
                                    setIsEditingEmailContent(true);
                                  }}
                                  aria-label="Edit email content"
                                >
                                  <Edit3 className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
                                </button>
                              )}
                            </div>
                            {isEditingEmailContent ? (
                              <RichTextEditor
                                value={emailEditorValue}
                                onChange={setEmailEditorValue}
                                height={300}
                                className="w-full"
                              />
                            ) : (
                              <EmailContentWithPortalTooltips htmlContent={enhanceLinkTooltips(selectedEmail.content)} />
                            )}
                          </div>
                        ) : (
                          <EmailContentWithPortalTooltips htmlContent={enhanceLinkTooltips(selectedEmail.content)} />
                        )}
                      </FontWrapper>
                    </div>

                    {/* Email Attachments - Mobile */}
                    {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                      <AttachmentComponent attachments={selectedEmail.attachments} />
                    )}
                    </div>

                    {isCardPresentation ? (
                      <div className={inboxCardLayout.footer}>
                        <PhishingReportButton
                          text={config.texts.phishingReportLabel}
                          onClick={handlePhishingReportClick}
                          disabled={!selectedEmail || allEmailsReported}
                          icon={<PhishingIcon />}
                          variant="emphasis"
                        />
                      </div>
                    ) : null}

                  </motion.div>
                </AnimatePresence>
              </div>
            ) : (
              <div className="overflow-y-auto relative flex-1" style={{ scrollbarWidth: 'none' }}>
                {config.emails.map((email, index) => (
                  <motion.div
                    key={email.id}
                    className={`p-4 border-b border-white/30 dark:border-white/10 cursor-pointer glass-border-no-radius transition-colors ${selectedEmailId === email.id ? 'lg:border-l-[7px] border-l-white/40 dark:border-l-white' : ''
                      } ${reportedEmails.has(email.id) ? 'opacity-60' : ''}`}
                    style={{ height: isEditMode ? 'auto' : '105px', minHeight: '105px' }}
                    onClick={() => handleEmailSelect(email.id)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className={`flex ${isEditMode ? 'flex-col gap-2' : 'items-start justify-between'}`}>
                      <div className={isEditMode ? '' : 'flex-1 min-w-0'}>
                        <div className={`flex items-center gap-2 mb-1 ${isEditMode ? '' : 'min-w-0'}`}>
                          <FontWrapper className={isEditMode ? '' : 'truncate min-w-0'}>
                            <EditableText
                              configPath={`emails.${index}.sender`}
                              className={`font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] ${isEditMode ? '' : 'truncate'} lg:mt-3 text-lg xl:mt-0`}
                              placeholder="Enter sender name"
                            >
                              {email.sender}
                            </EditableText>
                          </FontWrapper>
                          {reportedEmails.has(email.id) && (
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center bg-white/10 dark:bg-white/5 flex-shrink-0 ml-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'}`}>
                              {reportResults.get(email.id) ? (
                                <Check className="w-3 h-3 text-[#1C1C1E] dark:text-[#F2F2F7]" />
                              ) : (
                                <X className="w-3 h-3 text-[#1C1C1E] dark:text-[#F2F2F7]" />
                              )}
                            </div>
                          )}
                        </div>
                        <FontWrapper>
                          <EditableText
                            configPath={`emails.${index}.subject`}
                            className={`text-sm font-medium text-[#1C1C1E] dark:text-[#F2F2F7] ${isEditMode ? '' : 'truncate'} mb-1 block`}
                            placeholder="Enter email subject"
                          >
                            {email.subject}
                          </EditableText>
                          {reportedEmails.has(email.id) ? (
                            reportResults.get(email.id) ? (
                              <p className="text-xs text-[#1C1C1E]/80 dark:text-[#F2F2F7]/70 truncate">{config.texts.correctReportMessage}</p>
                            ) : (
                              <p className="text-xs text-[#1C1C1E]/80 dark:text-[#F2F2F7]/70 truncate">{config.texts.cautiousReportMessage}</p>
                            )
                          ) : (
                            <EditableText
                              configPath={`emails.${index}.preview`}
                              className={`text-xs text-[#1C1C1E] dark:text-[#F2F2F7] ${isEditMode ? '' : 'truncate'} block`}
                              placeholder="Enter email preview"
                            >
                              {email.preview}
                            </EditableText>
                          )}
                        </FontWrapper>
                      </div>
                      <div className={`flex items-center gap-2 shrink-0 whitespace-nowrap ${isEditMode ? '' : 'ml-2'}`}>
                        <EditableText
                          configPath={`emails.${index}.timestamp`}
                          className="text-xs text-[#1C1C1E] dark:text-[#F2F2F7] whitespace-nowrap"
                          placeholder="Enter timestamp"
                        >
                          {email.timestamp}
                        </EditableText>
                        <ChevronRight className="w-4 h-4 text-[#1C1C1E] dark:text-[#F2F2F7]" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Email Viewer - Desktop only */}
          <div className={`${isCardPresentation ? "hidden" : "hidden lg:block"} lg:col-span-2 glass-border-no-radius flex overflow-y-auto`} style={{ backgroundColor: "rgba(255, 255, 255, 0.3)" }}>
            <div ref={emailContentRef} className="p-6 overflow-y-auto scrollbar-hide flex-1" style={{ maxHeight: `${config.emails.length * 105}px` }}>
              {selectedEmail ? (
                <div>
                  {/* Email Header */}
                  <div className="flex items-start justify-between pb-4 border-b mb-4 border-white/50 dark:border-white/10">
                    <div className="flex-1">
                      <FontWrapper>
                        {isEditMode ? (
                          <EditableText
                            configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.subject`}
                            className="text-lg font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] mb-2 block"
                            placeholder="Enter email subject"
                          >
                            {selectedEmail.subject}
                          </EditableText>
                        ) : (
                          <h3 className="text-lg font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] mb-2">{selectedEmail.subject}</h3>
                        )}
                        <div className="text-sm text-[#1C1C1E] dark:text-[#F2F2F7] space-y-1">
                          <p><span className="font-semibold">{config.texts.fromLabel ?? "From:"}</span> {isEditMode ? (
                            <EditableText
                              configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.sender`}
                              className="inline text-sm text-[#1C1C1E] dark:text-[#F2F2F7]"
                              placeholder="Enter sender name"
                            >
                              {selectedEmail.sender}
                            </EditableText>
                          ) : selectedEmail.sender}</p>
                          <p><span className="font-semibold">{config.texts.timeLabel ?? "Time:"}</span> {isEditMode ? (
                            <EditableText
                              configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.timestamp`}
                              className="inline text-sm text-[#1C1C1E] dark:text-[#F2F2F7]"
                              placeholder="Enter timestamp"
                            >
                              {selectedEmail.timestamp}
                            </EditableText>
                          ) : selectedEmail.timestamp}</p>
                        </div>
                      </FontWrapper>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setShowHeaders(!showHeaders)}
                        className={`flex items-center gap-2 px-3 py-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} text-sm font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] hover:bg-white/5 dark:hover:bg-white/5 transition-colors`}
                      >
                        {showHeaders ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        <FontWrapper>{config.texts.headersButtonText}</FontWrapper>
                      </button>
                      <button className={`p-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} text-[#1C1C1E] dark:text-[#F2F2F7] hover:bg-white/5 dark:hover:bg-white/5 transition-colors`}>
                        <Star className="w-4 h-4" />
                      </button>
                      <button className={`p-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} text-[#1C1C1E] dark:text-[#F2F2F7] hover:bg-white/5 dark:hover:bg-white/5 transition-colors`}>
                        <FileText className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Email Headers */}
                  {showHeaders && selectedEmail.headers && (
                    <div className="mb-2 pb-4 border-b border-white/80 dark:border-white/10">
                      <FontWrapper>
                        <h4 className="text-sm font-semibold text-[#1C1C1E] dark:text-[#F2F2F7] mb-3">{config.texts.emailHeadersTitle}</h4>
                        <div className={`${isEditMode ? 'glass-border-2-no-overflow' : 'glass-border-2'} rounded-lg p-3`}>
                          {selectedEmail.headers.map((header, index) => (
                            <div key={index} className="text-xs text-[#1C1C1E] dark:text-[#F2F2F7] mb-1">
                              {isEditMode ? (
                                <EditableText
                                  configPath={`emails.${config.emails.findIndex(e => e.id === selectedEmail.id)}.headers.${index}`}
                                  className="text-xs text-[#1C1C1E] dark:text-[#F2F2F7] block"
                                  placeholder="Enter email header..."
                                >
                                  {header}
                                </EditableText>
                              ) : (
                                header
                              )}
                            </div>
                          ))}
                        </div>
                      </FontWrapper>
                    </div>
                  )}

                  {/* Email Content */}
                  <div className="prose prose-sm max-w-none mb-6 prose-p:text-[#1C1C1E] dark:prose-p:text-[#F2F2F7] prose-p:mb-1 prose-div:mb-1 prose-strong:text-[#1C1C1E] dark:prose-strong:text-[#F2F2F7] prose-li:text-[#1C1C1E] dark:prose-li:text-[#F2F2F7] prose-a:text-[#1C1C1E] dark:prose-a:text-[#F2F2F7] email-content-container">
                    <FontWrapper>
                      {isEditMode ? (
                        <div>
                          <div className="flex justify-end mb-2 gap-2">
                            {isEditingEmailContent ? (
                              <>
                                <button
                                  className={`px-3 py-1 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded`}
                                  onClick={() => {
                                    const emailIndex = config.emails.findIndex(e => e.id === selectedEmail.id);
                                    if (emailIndex >= 0 && updateTempConfigFn) {
                                      updateTempConfigFn(`emails.${emailIndex}.content`, emailEditorValue);
                                    }
                                    setIsEditingEmailContent(false);
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  className={`px-3 py-1 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} rounded`}
                                  onClick={() => setIsEditingEmailContent(false)}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className={`p-2 ${isEditMode ? 'glass-border-4-no-overflow' : 'glass-border-4'} text-[#1C1C1E] dark:text-[#F2F2F7] hover:bg-white/5 dark:hover:bg-white/5 transition-colors rounded-full`}
                                onClick={() => {
                                  setEmailEditorValue(selectedEmail.content || '');
                                  setIsEditingEmailContent(true);
                                }}
                                aria-label="Edit email content"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {isEditingEmailContent ? (
                            <RichTextEditor
                              value={emailEditorValue}
                              onChange={setEmailEditorValue}
                              height={300}
                              className="w-full"
                            />
                          ) : (
                            <EmailContentWithPortalTooltips htmlContent={enhanceLinkTooltips(selectedEmail.content)} />
                          )}
                        </div>
                      ) : (
                        <EmailContentWithPortalTooltips htmlContent={enhanceLinkTooltips(selectedEmail.content)} />
                      )}
                    </FontWrapper>
                  </div>

                  {/* Email Attachments - Desktop */}
                  {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <AttachmentComponent attachments={selectedEmail.attachments} />
                  )}

                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-20">
                  <Mail className="w-16 h-16 text-[#1C1C1E] dark:text-[#F2F2F7] mx-auto mb-4" />
                  <FontWrapper>
                    <h3 className="text-lg font-medium text-[#1C1C1E] dark:text-[#F2F2F7] mb-2">{config.texts.selectEmailMessage}</h3>
                    <p className="text-[#1C1C1E] dark:text-[#F2F2F7]">{config.texts.instructions}</p>
                  </FontWrapper>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phishing Report Modal */}
      <PhishingReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onReport={handleModalReport}
        modalTexts={config.texts.phishingReportModal}
      />

      {/* Phishing Result Modal */}
      {lastReportedEmail && (
        <PhishingResultModal
          isOpen={showResultModal}
          onClose={() => {
            setShowResultModal(false);
            // On mobile, return to inbox list after reporting
            if (isMobile) {
              setSelectedEmailId(null);
              onSelectedEmailIdChange?.(null);
            }
          }}
          modalTexts={config.texts.phishingResultModal}
          email={lastReportedEmail}
          isCorrect={lastReportCorrect}
        />
      )}

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        attachment={previewAttachment}
        isOpen={showPreviewModal}
        onClose={closePreviewModal}
        isEditMode={isEditMode}
      />

      {/* Attachment Edit Modal */}
      {selectedEmail && editingAttachment && (
        <AttachmentEditModal
          isOpen={showAttachmentEditModal}
          initialValue={selectedEmail.attachments?.[editingAttachment.attIndex]?.content || ''}
          onClose={() => {
            setShowAttachmentEditModal(false);
            setEditingAttachment(null);
          }}
          onSave={(newHtml) => {
            const { emailIndex, attIndex } = editingAttachment;
            if (updateTempConfigFn) {
              updateTempConfigFn(`emails.${emailIndex}.attachments.${attIndex}.content`, newHtml);
            } else {
              console.warn('EditModeProvider not available for attachment content update');
            }
            setShowAttachmentEditModal(false);
            setEditingAttachment(null);
          }}
          title="Edit Attachment Content"
        />
      )}

      {/* Inline editor handles email content editing; no extra modal needed */}
    </div>
  );
}